import { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Checkbox, Tooltip } from '@material-ui/core';
import { GetAppOutlined } from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import {
  downloadAllAttachments,
  getAttachmentDownloadUrl,
} from 'src/Helpers/FileDownloadUtils';

AttachmentDownloadAllButton.propTypes = {
  files: PropTypes.array,
  allFiles: PropTypes.array,
  onToggleSelectAll: PropTypes.func,
  onEmptyClick: PropTypes.func,
  onDownloadComplete: PropTypes.func,
  onDownload: PropTypes.func,
  isFileDownloadable: PropTypes.func,
  selectionMode: PropTypes.bool,
  hidden: PropTypes.bool,
  label: PropTypes.string,
  variant: PropTypes.string,
  color: PropTypes.string,
  size: PropTypes.string,
  sx: PropTypes.object,
};

const isDownloadRestrictedUser = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userType = user?.isClient ? 3 : user?.user_type;
    return Boolean(user?.isClient || userType === 3);
  } catch {
    return false;
  }
};

const getFileKey = (file) =>
  getAttachmentDownloadUrl(file) ||
  file?.url ||
  file?.name ||
  file?.preview ||
  file?.file_url ||
  '';

export default function AttachmentDownloadAllButton({
  files = [],
  allFiles = null,
  onToggleSelectAll = null,
  onEmptyClick = null,
  onDownloadComplete = null,
  onDownload = null,
  isFileDownloadable = null,
  selectionMode = false,
  hidden = false,
  label = 'Download All',
  variant = 'outlined',
  color = 'primary',
  size = 'medium',
  sx = {},
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [isDownloading, setIsDownloading] = useState(false);

  if (hidden || isDownloadRestrictedUser()) return null;

  const canDownloadFile =
    typeof isFileDownloadable === 'function'
      ? isFileDownloadable
      : (file) => Boolean(getAttachmentDownloadUrl(file));

  const downloadableFiles = (Array.isArray(files) ? files : []).filter((file) =>
    Boolean(canDownloadFile(file)),
  );

  const selectableFiles = Array.isArray(allFiles) ? allFiles : [];
  const showSelectAll =
    typeof onToggleSelectAll === 'function' &&
    selectableFiles.length > 0 &&
    (downloadableFiles.length > 0 || selectionMode);

  const allSelected =
    showSelectAll &&
    selectableFiles.length > 0 &&
    selectableFiles.every((file) => {
      const key = getFileKey(file);
      if (!key) return false;
      return files.some((selected) => getFileKey(selected) === key);
    });
  const someSelected = showSelectAll && downloadableFiles.length > 0 && !allSelected;

  const clearSelection = () => {
    if (typeof onToggleSelectAll === 'function') {
      onToggleSelectAll(false);
    }
    if (typeof onDownloadComplete === 'function') {
      onDownloadComplete();
    }
  };

  const handleClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (!downloadableFiles.length) {
      if (typeof onEmptyClick === 'function') {
        onEmptyClick();
        return;
      }
      enqueueSnackbar(
        String(label).toLowerCase().includes('selected')
          ? 'Please select files to download'
          : 'No files available to download',
        { variant: 'warning' },
      );
      return;
    }

    setIsDownloading(true);
    enqueueSnackbar(
      `Downloading ${downloadableFiles.length} file${
        downloadableFiles.length === 1 ? '' : 's'
      }...`,
      { variant: 'info' },
    );

    try {
      let result;
      if (typeof onDownload === 'function') {
        result = await onDownload(downloadableFiles);
      } else {
        result = await downloadAllAttachments(downloadableFiles);
      }

      const successCount = Number(result?.successCount ?? 0);
      const failCount = Number(result?.failCount ?? 0);
      const total = Number(result?.total ?? downloadableFiles.length);

      if (successCount === total && total > 0) {
        enqueueSnackbar(
          `Downloaded ${successCount} file${successCount === 1 ? '' : 's'} successfully`,
          { variant: 'success' },
        );
        clearSelection();
      } else if (successCount > 0) {
        enqueueSnackbar(
          `Downloaded ${successCount} of ${total} files. ${failCount} failed.`,
          { variant: 'warning' },
        );
        clearSelection();
      } else {
        enqueueSnackbar('Failed to download files', { variant: 'error' });
      }
    } catch (error) {
      console.error('Download all failed:', error);
      enqueueSnackbar('Failed to download files', { variant: 'error' });
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadButton = (
    <Button
      variant={selectionMode && downloadableFiles.length === 0 ? 'contained' : variant}
      color={color}
      size={size}
      startIcon={<GetAppOutlined />}
      onClick={handleClick}
      disabled={
        isDownloading ||
        (downloadableFiles.length === 0 && typeof onEmptyClick !== 'function')
      }
      sx={sx}
    >
      {isDownloading
        ? 'Downloading...'
        : `${label} (${downloadableFiles.length})`}
    </Button>
  );

  if (!showSelectAll) return downloadButton;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title={allSelected ? 'Deselect All' : 'Select All'}>
        <Checkbox
          size="small"
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(e) => {
            e.stopPropagation();
            onToggleSelectAll(e.target.checked);
          }}
          onClick={(e) => e.stopPropagation()}
        />
      </Tooltip>
      {downloadButton}
    </Box>
  );
}
